/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { type ComponentProps } from 'react';

import type { CareerRole_PathwayLevel as ProficiencyLevel } from '__generated__/graphql-types';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CondensedCareerRoleCard from 'bundles/browse-common/components/CondensedCareerRoleCard';
import CareerAcademyModule, { type Filter } from 'bundles/unified-common/modules/CareerAcademyModule';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';

import _t from 'i18n!nls/browse-common';

const styles = {
  container: css`
    .rc-ProductCardCollectionPlaceholder .cds-ProductCard-grid {
      min-height: unset !important;
      max-height: 280px;
      overflow: hidden;
    }

    a.cds-button-secondary {
      /* Button span across width on mobile */
      ${breakpoints.down('xs')} {
        width: 100%;
      }
    }
  `,
  wrapper: css`
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
  contentOffset: css`
    /* Manually adjust the heading margin-top since we use condensed cards */
    margin-top: calc(var(--cds-spacing-600));
    ${breakpoints.down('sm')} {
      margin-top: 0;
    }
  `,
};

type DropdownMenu = Required<ComponentProps<typeof CareerAcademyModule>>['dropdownMenu'];

type RelatedRolesComponentProps = {
  isCollectionLoading: boolean;
  collection: CareerRolesCollectionType | undefined;

  setSelectedDropdownValue?: (value: ProficiencyLevel) => void;
  setCurrentChipFilterIndex?: (index: number) => void;

  chipFilters?: Filter[];
  dropdownMenuProps?: DropdownMenu;
  currentChipFilterIndex?: number;
  currentSelectedDropdownValue?: ProficiencyLevel;

  hideFilters?: boolean;
  headline: string;
  description: string;
};

const RelatedRolesComponent = (props: RelatedRolesComponentProps) => {
  const {
    setSelectedDropdownValue = () => {},
    setCurrentChipFilterIndex = () => {},
    isCollectionLoading,
    collection,
    chipFilters,
    dropdownMenuProps,
    currentChipFilterIndex,
    currentSelectedDropdownValue,
    hideFilters = false,
    headline,
    description,
  } = props;

  const track = useTracker();

  if (collection && (collection.entities?.length ?? 0) === 0) {
    return null;
  }

  const trackCtaClick = () => {
    track('click_button', {
      button: {
        name: 'career_academy_cta',
        linkName: 'explore_all_roles',
        linkType: 'internal',
        linkURL: '/career-academy',
      },
    });
  };

  const handleFilterChange = (index: number) => {
    setCurrentChipFilterIndex(index);
  };

  const handleDropdownChange = (value: ProficiencyLevel) => {
    setSelectedDropdownValue(value);
  };

  const filterProps = hideFilters
    ? {}
    : {
        filters: chipFilters,
        dropdownMenu: dropdownMenuProps,
        currentFilterIndex: currentChipFilterIndex,
        currentDropdownValue: currentSelectedDropdownValue,
        onFilterChange: handleFilterChange,
        onDropdownMenuChange: handleDropdownChange,
      };

  return (
    <PageGridContainer component="section" css={styles.container}>
      <Grid item xs={12}>
        <div css={styles.wrapper}>
          <CareerAcademyModule
            headline={headline}
            description={description}
            collection={collection}
            cta={{
              text: _t('View all'),
              target: '/career-academy',
              variant: 'secondary',
              onClick: trackCtaClick,
            }}
            ProductCardComponent={CondensedCareerRoleCard}
            layout="text3/image9"
            backgroundColor="grey"
            showRoundedCorners
            isPageGridContainerAppliedOnPage={true}
            showAllEntries
            isCollectionLoading={isCollectionLoading}
            numOfEntriesPerCollection={2}
            defaultMobileRows={2}
            overrideItemsGridConfig={{ lg: 6, md: 6, sm: 6, xs: 12 }}
            overrideContentOffsets={styles.contentOffset}
            {...filterProps}
          />
        </div>
      </Grid>
    </PageGridContainer>
  );
};

export default RelatedRolesComponent;
